/**
 * NightKit.js
 *
 * "Daily activity" building blocks of the Sofőr (driver) app: an indigo canvas
 * header (greeting, date strip, metric tiles) with the screen content riding in
 * a rounded sheet beneath it — the structure of the fitness-tracker concept
 * (dark top, light sheet), re-skinned with the orange brand accent.
 *
 *  - {@link NightScreen}  — scroll shell: night header block + rounded content sheet;
 *  - {@link NightHeader}  — avatar / eyebrow / title / actions row for the header block;
 *  - {@link DateStrip}    — horizontal day chips (selected day = orange capsule);
 *  - {@link NightMetric}  — icon + value + label tile on the night canvas;
 *  - {@link NightCard}    — navy rounded card (route summary, work time…);
 *  - {@link RingGauge}    — circular progress with a centre label.
 *
 * Presentation only; callers own data and navigation.
 *
 * @module components/premium/NightKit
 */

import React from 'react';
import {Pressable, RefreshControl, ScrollView, StyleSheet, Text, View, useWindowDimensions} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Svg, {Circle} from 'react-native-svg';
import Icon from '../management/SafeIcon';
import Avatar from '../Avatar';
import {BRAND, NIGHT, useManagementTheme} from '../../helpers/managementTheme';
import {useBottomInset, useTopInset} from '../../helpers/safeArea';

/** Space reserved at the bottom of the sheet for the floating tab bar (px). */
const TAB_CLEARANCE = 128;

/**
 * Header row for the night block: avatar, eyebrow + title, right-hand actions.
 *
 * @param {object} props
 * @param {object|string} [props.user] User (or name) for the avatar; omit to hide it.
 * @param {Function} [props.onAvatarPress] Avatar tap handler.
 * @param {string} [props.eyebrow] Small muted line (date, greeting).
 * @param {string} props.title Bold heading.
 * @param {React.ReactNode} [props.right] Action buttons.
 * @returns {JSX.Element} The header row.
 */
export const NightHeader = ({user, onAvatarPress, eyebrow, title, right}) => (
    <View style={styles.headerRow}>
        <View style={styles.headerText}>
            {!!eyebrow && <Text style={styles.headerEyebrow} numberOfLines={1}>{eyebrow}</Text>}
            <Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>
        </View>
        {right}
        {!!user && (
            <Pressable onPress={onAvatarPress} disabled={!onAvatarPress} accessibilityRole="button" style={styles.headerAvatar}>
                <Avatar user={user} size={42} />
            </Pressable>
        )}
    </View>
);

/**
 * Horizontal day strip. The selected day is an orange capsule, others are navy
 * chips; a dot marks days that carry work.
 *
 * @param {object} props
 * @param {Array<{key: string, weekday: string, day: string|number, selected?: boolean, marked?: boolean, disabled?: boolean}>} props.days Days left → right.
 * @param {Function} [props.onSelect] `(key) => void`.
 * @returns {JSX.Element} The strip.
 */
export const DateStrip = ({days = [], onSelect}) => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.dateStrip}>
        {days.map((d) => {
            const chip = (
                <>
                    <Text style={[styles.dateWeekday, d.selected && styles.dateTextOn]}>{d.weekday}</Text>
                    <Text style={[styles.dateDay, d.selected && styles.dateTextOn]}>{d.day}</Text>
                    {d.marked && !d.selected && <View style={styles.dateDot} />}
                </>
            );

            return (
                <Pressable
                    key={d.key}
                    onPress={() => onSelect?.(d.key)}
                    disabled={d.disabled}
                    accessibilityRole="button"
                    accessibilityState={{selected: !!d.selected, disabled: !!d.disabled}}
                    style={({pressed}) => [styles.dateChipWrap, pressed && {opacity: 0.85}, d.disabled && {opacity: 0.4}]}
                >
                    {d.selected ? (
                        <LinearGradient colors={BRAND.gradient} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.dateChip}>{chip}</LinearGradient>
                    ) : (
                        <View style={[styles.dateChip, styles.dateChipIdle]}>{chip}</View>
                    )}
                </Pressable>
            );
        })}
    </ScrollView>
);

/**
 * Metric tile on the night canvas: glyph, bold value (+ unit) and a muted label.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string|number} props.value Bold value.
 * @param {string} [props.unit] Unit suffix next to the value.
 * @param {string} props.label Muted caption.
 * @param {string} [props.color] Glyph colour (defaults to the brand orange).
 * @param {Function} [props.onPress] Tap handler.
 * @returns {JSX.Element} The tile.
 */
export const NightMetric = ({icon, value, unit, label, color = BRAND.light, onPress}) => (
    <Pressable onPress={onPress} disabled={!onPress} style={({pressed}) => [styles.metric, pressed && {opacity: 0.85}]}>
        <Icon name={icon} size={20} color={color} />
        <View style={styles.metricValueRow}>
            <Text style={styles.metricValue} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{value == null ? '—' : String(value)}</Text>
            {!!unit && <Text style={styles.metricUnit} numberOfLines={1}>{unit}</Text>}
        </View>
        <Text style={styles.metricLabel} numberOfLines={1}>{label}</Text>
    </Pressable>
);

/**
 * Navy rounded card for summaries on the night canvas.
 *
 * @param {object} props
 * @param {string} [props.title] Card title.
 * @param {string} [props.subtitle] Muted line beside the title.
 * @param {React.ReactNode} [props.right] Right-aligned header content.
 * @param {React.ReactNode} props.children Card body.
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The card.
 */
export const NightCard = ({title, subtitle, right, children, style}) => (
    <View style={[styles.card, style]}>
        {(!!title || !!right) && (
            <View style={styles.cardHeader}>
                <View style={styles.headerText}>
                    {!!title && <Text style={styles.cardTitle} numberOfLines={1}>{title}</Text>}
                    {!!subtitle && <Text style={styles.cardSubtitle} numberOfLines={1}>{subtitle}</Text>}
                </View>
                {right}
            </View>
        )}
        {children}
    </View>
);

/**
 * Circular progress gauge with a centre label.
 *
 * @param {object} props
 * @param {number} props.progress 0..1 fill.
 * @param {number} [props.size=76] Diameter in px.
 * @param {number} [props.strokeWidth=8] Ring thickness in px.
 * @param {string} [props.color] Progress colour (defaults to the brand orange).
 * @param {string} [props.track] Track colour.
 * @param {React.ReactNode} [props.children] Centre content.
 * @returns {JSX.Element} The gauge.
 */
export const RingGauge = ({progress, size = 76, strokeWidth = 8, color = BRAND.base, track = 'rgba(255,255,255,0.12)', children}) => {
    const clamped = Math.max(0, Math.min(1, Number(progress) || 0));
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    return (
        <View style={{width: size, height: size, alignItems: 'center', justifyContent: 'center'}}>
            <Svg width={size} height={size} style={StyleSheet.absoluteFill}>
                <Circle cx={size / 2} cy={size / 2} r={radius} stroke={track} strokeWidth={strokeWidth} fill="none" />
                <Circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${circumference * clamped} ${circumference}`}
                    rotation="-90"
                    originX={size / 2}
                    originY={size / 2}
                />
            </Svg>
            {children}
        </View>
    );
};

/**
 * Night screen shell. The whole page scrolls: an indigo gradient header block
 * (`header`) on top, then the content inside a rounded sheet that overlaps it.
 * Pull-to-refresh is supported; the sheet keeps clear of the floating tab bar.
 *
 * @param {object} props
 * @param {React.ReactNode} props.header Header-block content (NightHeader, DateStrip, metrics…).
 * @param {React.ReactNode} props.children Sheet content.
 * @param {boolean} [props.refreshing] Pull-to-refresh state.
 * @param {Function} [props.onRefresh] Pull-to-refresh handler (disables the control when omitted).
 * @param {number} [props.bottomClearance] Override for the space under the sheet content.
 * @param {object} [props.scrollRef] Ref forwarded to the ScrollView.
 * @returns {JSX.Element} The shell.
 */
export const NightScreen = ({header, children, refreshing = false, onRefresh, bottomClearance, scrollRef}) => {
    const m = useManagementTheme();
    const topInset = useTopInset();
    const bottomInset = useBottomInset();
    const {height} = useWindowDimensions();
    const sheetColor = m.colors.bg;

    return (
        <ScrollView
            ref={scrollRef}
            style={{flex: 1, backgroundColor: sheetColor}}
            contentContainerStyle={{flexGrow: 1}}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshControl={onRefresh ? <RefreshControl refreshing={!!refreshing} onRefresh={onRefresh} tintColor={BRAND.base} progressViewOffset={topInset} /> : undefined}
        >
            <LinearGradient colors={[NIGHT.bg, NIGHT.bgDeep]} start={{x: 0, y: 0}} end={{x: 0, y: 1}} style={[styles.headerBlock, {paddingTop: topInset + 12}]}>
                <View pointerEvents="none" style={styles.nightGlowA} />
                <View pointerEvents="none" style={styles.nightGlowB} />
                {header}
            </LinearGradient>
            <View style={[styles.sheet, {backgroundColor: sheetColor, minHeight: height * 0.62, paddingBottom: bottomClearance ?? (TAB_CLEARANCE + bottomInset)}]}>
                {children}
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    headerBlock: {paddingHorizontal: 18, paddingBottom: 44, overflow: 'visible'},
    nightGlowA: {position: 'absolute', width: 260, height: 260, borderRadius: 130, backgroundColor: 'rgba(249,115,22,0.10)', top: -120, right: -90},
    nightGlowB: {position: 'absolute', width: 200, height: 200, borderRadius: 100, backgroundColor: 'rgba(99,102,241,0.18)', bottom: -60, left: -80},
    sheet: {marginTop: -28, borderTopLeftRadius: 32, borderTopRightRadius: 32, paddingTop: 20, paddingHorizontal: 16},

    headerRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
    headerText: {flex: 1, minWidth: 0},
    headerEyebrow: {color: NIGHT.subText, fontSize: 13, fontWeight: '600'},
    headerTitle: {color: NIGHT.text, fontSize: 26, fontWeight: '800', letterSpacing: -0.6, marginTop: 2},
    headerAvatar: {borderWidth: 2, borderColor: BRAND.base, borderRadius: 25, padding: 2},

    dateStrip: {gap: 10, paddingVertical: 16, paddingRight: 8},
    dateChipWrap: {},
    dateChip: {width: 62, height: 78, borderRadius: 18, alignItems: 'center', justifyContent: 'center', gap: 4},
    dateChipIdle: {backgroundColor: NIGHT.card, borderWidth: 1, borderColor: NIGHT.border},
    dateWeekday: {color: NIGHT.subText, fontSize: 12.5, fontWeight: '600'},
    dateDay: {color: NIGHT.text, fontSize: 22, fontWeight: '800'},
    dateTextOn: {color: '#FFFFFF'},
    dateDot: {position: 'absolute', bottom: 9, width: 5, height: 5, borderRadius: 3, backgroundColor: BRAND.light},

    metric: {flex: 1, minWidth: 0, backgroundColor: NIGHT.card, borderRadius: 20, paddingVertical: 14, paddingHorizontal: 12, borderWidth: 1, borderColor: NIGHT.border, gap: 8},
    metricValueRow: {flexDirection: 'row', alignItems: 'flex-end', gap: 4},
    metricValue: {color: NIGHT.text, fontSize: 24, fontWeight: '800', letterSpacing: -0.5, flexShrink: 1},
    metricUnit: {color: NIGHT.subText, fontSize: 12, fontWeight: '600', marginBottom: 3},
    metricLabel: {color: NIGHT.subText, fontSize: 12, fontWeight: '600'},

    card: {backgroundColor: NIGHT.card, borderRadius: 24, padding: 16, borderWidth: 1, borderColor: NIGHT.border},
    cardHeader: {flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12},
    cardTitle: {color: NIGHT.text, fontSize: 16, fontWeight: '800'},
    cardSubtitle: {color: NIGHT.subText, fontSize: 12.5, fontWeight: '500', marginTop: 2},
});
